import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Swords, MessageCircle, Bell, User, LogOut,
  Menu, X, ChevronRight, Users
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const NAV_ITEMS = [
  { to: '/app/feed', icon: Swords, label: 'Rivalries', badge: null },
  { to: '/app/teams', icon: Users, label: 'Teams', badge: null },
  { to: '/app/chat', icon: MessageCircle, label: 'Chat', badge: 'chat' },
  { to: '/app/notifications', icon: Bell, label: 'Notifications', badge: 'notif' },
  { to: '/app/profile', icon: User, label: 'Profile', badge: null },
];

export default function AppLayout() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);

  useEffect(() => {
    if (!token) return;
    const fetchCounts = () => {
      api.getUnreadCount(token).then(d => setUnreadNotif(d?.count ?? d?.unread_count ?? 0)).catch(() => {});
      api.getUnreadChatCount(token).then(d => setUnreadChat(d?.count ?? d?.unread_count ?? 0)).catch(() => {});
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const avatar = user?.profile_picture || user?.photo || user?.avatar || null;
  const displayName = user?.username || user?.first_name || user?.email?.split('@')[0] || 'You';
  const userHandle = user?.username ? `@${user.username}` : user?.email || '';

  const getBadge = (badgeKey) => {
    if (badgeKey === 'chat') return unreadChat;
    if (badgeKey === 'notif') return unreadNotif;
    return 0;
  };

  return (
    <div className="app-root">
      {/* ── Sidebar (desktop) ── */}
      <aside className={`app-sidebar ${sidebarOpen ? 'sidebar-expanded' : ''}`}>
        <div className="sidebar-logo-wrap">
          <Swords size={26} color="#0EA5E9" className="sidebar-logo" />
          {sidebarOpen && <span className="sidebar-logo-text">RIVALCAM</span>}
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ to, icon: Icon, label, badge }) => {
            const count = getBadge(badge);
            return (
              <NavLink key={to} to={to} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}>
                <div className="sidebar-icon-wrap">
                  <Icon size={20} />
                  {count > 0 && <span className="sidebar-badge">{count > 99 ? '99+' : count}</span>}
                </div>
                {sidebarOpen && <span className="sidebar-label">{label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-bottom">
          <button className="sidebar-link sidebar-expand-btn" onClick={() => setSidebarOpen(o => !o)}>
            <ChevronRight size={18} className={sidebarOpen ? 'rotate-180' : ''} />
            {sidebarOpen && <span className="sidebar-label">Collapse</span>}
          </button>
          <button className="sidebar-link sidebar-logout" onClick={handleLogout}>
            <LogOut size={20} />
            {sidebarOpen && <span className="sidebar-label">Sign out</span>}
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="app-main">
        {/* ── Top bar (mobile) ── */}
        <header className="app-topbar">
          <button className="topbar-hamburger" onClick={() => setMobileNavOpen(o => !o)}>
            {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Swords size={22} color="#0EA5E9" className="topbar-logo" />
          <div className="topbar-avatar">
            {avatar ? (
              <img src={avatar} alt={displayName} className="topbar-avatar-img"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            ) : null}
            <div className="topbar-avatar-fallback" style={{ display: avatar ? 'none' : 'flex' }}>
              {displayName[0]?.toUpperCase()}
            </div>
          </div>
        </header>

        {/* ── Mobile nav drawer ── */}
        {mobileNavOpen && (
          <div className="mobile-nav-overlay" onClick={() => setMobileNavOpen(false)}>
            <div className="mobile-nav-drawer" onClick={e => e.stopPropagation()}>
              <div className="mobile-nav-user">
                <div className="mobile-nav-avatar">
                  {avatar ? (
                    <img src={avatar} alt={displayName} className="mobile-nav-avatar-img" />
                  ) : (
                    <span>{displayName[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <div className="mobile-nav-name">{displayName}</div>
                  <div className="mobile-nav-handle">{userHandle}</div>
                </div>
              </div>
              <nav className="mobile-nav-items">
                {NAV_ITEMS.map(({ to, icon: Icon, label, badge }) => {
                  const count = getBadge(badge);
                  return (
                    <NavLink key={to} to={to}
                      className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                      onClick={() => setMobileNavOpen(false)}>
                      <Icon size={20} />
                      <span>{label}</span>
                      {count > 0 && <span className="mobile-nav-badge">{count}</span>}
                    </NavLink>
                  );
                })}
              </nav>
              <button className="mobile-nav-logout" onClick={handleLogout}>
                <LogOut size={18} />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Page content ── */}
        <div className="app-page">
          <Outlet />
        </div>

        {/* ── Bottom tab bar (mobile) ── */}
        <nav className="bottom-tabbar">
          {NAV_ITEMS.map(({ to, icon: Icon, label, badge }) => {
            const count = getBadge(badge);
            return (
              <NavLink key={to} to={to}
                className={({ isActive }) => `bottom-tab ${isActive ? 'active' : ''}`}>
                <div className="bottom-tab-icon">
                  <Icon size={22} />
                  {count > 0 && <span className="bottom-tab-badge">{count}</span>}
                </div>
                <span className="bottom-tab-label">{label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
